/*
 This file is part of GNU Anastasis
 (C) 2021-2022 Anastasis SARL

 GNU Anastasis is free software; you can redistribute it and/or modify it under the
 terms of the GNU Affero General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Anastasis is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details.

 You should have received a copy of the GNU Affero General Public License along with
 GNU Anastasis; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  StorageState,
  buildStorageKey,
  useLocalStorage,
} from "./useLocalStorage.js";

/**
 * If the translation is under this threshold then
 * browser won't automatically switch to default lang.
 *
 */
const MIN_LANG_COVERAGE_THRESHOLD = 85;
/**
 * choose the best from the browser config based on the completeness
 * on the translation
 */
function getBrowserLang(
  completeness: Record<string, number>,
): string | undefined {
  if (typeof window === "undefined") return undefined;

  const available = Object.keys(completeness);
  const resolveAvailable = (requested: string): string | undefined => {
    const normalized = requested.replaceAll("_", "-").toLowerCase();
    return (
      available.find((candidate) => candidate.toLowerCase() === normalized) ??
      available.find(
        (candidate) => candidate.toLowerCase() === normalized.split("-", 1)[0],
      )
    );
  };

  if (window.navigator.language) {
    const exact = resolveAvailable(window.navigator.language);
    if (exact && completeness[exact] >= MIN_LANG_COVERAGE_THRESHOLD) {
      return exact;
    }
  }
  if (window.navigator.languages) {
    const match = Object.entries(completeness)
      .filter(([code, value]) => {
        if (value < MIN_LANG_COVERAGE_THRESHOLD) return false; //do not consider langs below the threshold
        return window.navigator.languages.some(
          (requested) => resolveAvailable(requested) === code,
        );
      })
      .map(([code, value]) => ({ code, value }));

    if (match.length > 0) {
      let max = match[0];
      match.forEach((v) => {
        if (v.value > max.value) {
          max = v;
        }
      });
      return max.code;
    }
  }

  return undefined;
}

const langPreferenceKey = buildStorageKey("lang-preference");

export function useLang(
  initial: string | undefined,
  completeness: Record<string, number>,
): Required<StorageState> {
  const defaultValue = getBrowserLang(completeness) || initial || "en";
  return useLocalStorage(langPreferenceKey, defaultValue);
}
